import styled, { css, useTheme } from 'styled-components';
import { Typography } from '@elfsight/widget-ui';
import {
  createReadableClass,
  isTransparentColor
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useColumn } from '../columns/column-context';
import {
  columnElementsExistenceCheckMap,
  HORIZONTAL_PADDING,
  VERTICAL_PADDING
} from '../constants';
import { useTables } from '../tables-context';
import { ColumnElementBase } from './column-element-base';
import { type ColumnElement } from './column-elements';

const { features: checkIfFeaturesExist } = columnElementsExistenceCheckMap;

export function Title({
  $alignRows: alignRows,
  $isLastElement: isLastElement,
  ...forwardingProps
}: ColumnElement) {
  const { showFeatures } = useSettings();
  const { elements } = useTables();
  const column = useColumn();
  const {
    title: { backgroundColor },
    features: { type: featuresType }
  } = useTheme();

  const { title, titleCaption: caption } = column;

  const titleIndex = elements.findIndex((element) => element === 'title');
  const featuresIndex = elements.findIndex((element) => element === 'features');
  const doFeaturesExist = showFeatures && featuresIndex - 1 === titleIndex;
  const areFeaturesNext = alignRows
    ? doFeaturesExist
    : checkIfFeaturesExist(column) && doFeaturesExist;

  const isBackgroundTransparent = isTransparentColor(backgroundColor);
  const withBottomPadding =
    !isLastElement &&
    !isBackgroundTransparent &&
    (featuresType === 'plain' || !areFeaturesNext);

  return (
    <Container
      {...forwardingProps}
      $alignRows={alignRows}
      $isBackgroundTransparent={isBackgroundTransparent}
      $withBottomPadding={withBottomPadding}
    >
      {!!title && <Text>{title}</Text>}

      {!!caption && <Caption>{caption}</Caption>}
    </Container>
  );
}

interface ContainerProps extends ColumnElement {
  $isBackgroundTransparent: boolean;
  $withBottomPadding: boolean;
}

const Container = styled(ColumnElementBase).attrs<ContainerProps>({
  className: createReadableClass({ component: 'title', element: 'container' })
})`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({
    $isBackgroundTransparent,
    $additionalTopPadding = 0,
    theme: {
      title: { backgroundColor }
    }
  }) => css`
    padding: ${VERTICAL_PADDING * 2 + $additionalTopPadding}px
      ${HORIZONTAL_PADDING}px
      ${$isBackgroundTransparent ? VERTICAL_PADDING : VERTICAL_PADDING * 2}px
      ${HORIZONTAL_PADDING}px;
    background-color: ${backgroundColor};
  `}

  ${({ $withBottomPadding }) =>
    $withBottomPadding &&
    css`
      margin-bottom: ${VERTICAL_PADDING}px;
    `}
`;

const Text = styled(Typography).attrs(
  ({
    theme: {
      title: { alignment, color, font }
    }
  }) => ({
    color,
    textStyle: { ...font, textAlign: alignment },
    className: createReadableClass({ component: 'title' })
  })
)``;

const Caption = styled(Typography).attrs(
  ({
    theme: {
      title: { alignment, captionColor, captionFont }
    }
  }) => ({
    color: captionColor,
    textStyle: { ...captionFont, textAlign: alignment },
    className: createReadableClass({ component: 'title', element: 'caption' })
  })
)`
  margin-top: 4px;
`;
